import MapView from '@arcgis/core/views/MapView';
import { Layer as DeckGLLayer } from '@deck.gl/layers';
import { FULCRUM_DECK_LAYER_ID } from '../arc-gis-map-view';
import {
  LayerState, LayerAddFunction, LayerRemoveFunction, debugLog,
} from './utils/layer-change-coordinator';
import { convertGeoJsonLayer } from './utils/layer-conversions';
import { Layer } from '../../../models/layer-settings';
import { MapPlugin } from '../arcgis-types';

export const FULCRUM_DECK_GEOJSON_PREFIX = 'fulcrum-geojson-id-';

/**
 * GeoJsonLayers is responsible for GeoJsonLayers. We use @deck.gl packages because the
 * ArcGIS GeoJSONLayer doesn't support more than one Geometry type per layer.
 * At the same time, only one @deck.gl/arcgis Layer will be visible on the Map at a time.
 * Therefore, we manage the GeoJsonLayers within the `deckLayers.deck.layers` array.
 */
const GeoJsonLayers = (layerChangeCoordinator: LayerState): MapPlugin => {
  const getNewGeoJsonLayer = (layer: Layer) => {
    const geoJsonLayer = convertGeoJsonLayer(layer);
    geoJsonLayer.id = `${FULCRUM_DECK_GEOJSON_PREFIX}${layer.id}`;

    return geoJsonLayer;
  };

  const initLayers = (mapView: MapView) => {
    const geoJsonLayers = layerChangeCoordinator.activeOverlays().filter((layer) => layer.type === 'geojson');
    if (geoJsonLayers.length > 0) {
      const deckLayer = mapView.map.findLayerById(FULCRUM_DECK_LAYER_ID);
      if (!deckLayer) {
        throw new Error('DeckLayer not found!!!!');
      }

      [...geoJsonLayers].forEach((layer) => {
        const geoJsonLayer = getNewGeoJsonLayer(layer);
        debugLog('Initing deck layer', layer);
        (deckLayer as DeckGLLayer).deck.layers.push(geoJsonLayer);
      });
    }
  };

  return {
    extendMap: (mapView: MapView) => {
      if (!mapView) {
        return;
      }

      initLayers(mapView);

      const getDeckLayer = () => mapView.map?.findLayerById(FULCRUM_DECK_LAYER_ID) as DeckGLLayer;

      const removeLayer: LayerRemoveFunction = (layer: Layer) => {
        if (layer.type === 'geojson') {
          const deckLayer = getDeckLayer();
          if (!deckLayer) {
            return;
          }

          const remainingLayers = deckLayer.deck.layers.filter((l) => l.id !== `${FULCRUM_DECK_GEOJSON_PREFIX}${layer.id}`);
          debugLog('Removing deck layer', layer);
          deckLayer.deck.set('layers', remainingLayers);
        }
      };

      const addLayer: LayerAddFunction = (index: number, layer: Layer) => {
        const deckLayer = getDeckLayer();
        if (!deckLayer) {
          return;
        }

        if (layer.type === 'geojson') {
          const newLayer = getNewGeoJsonLayer(layer);
          const newLayers = [newLayer, ...deckLayer.deck.layers];
          debugLog('Adding deck layer', layer);
          deckLayer.deck.set('layers', newLayers); // using "set" causes the layer to re-render; "layers.push" does not
        }
      };

      layerChangeCoordinator.onLayerChange({ remove: removeLayer, add: addLayer });
    },
  };
};

export default GeoJsonLayers;
